import { constrain } from 'utils/math';
import { DOMHelper } from 'core/DOMHelper';
import { ScrollerType } from 'app/data/Enums';
import { ScrollbarViewDeps } from './scrollbar.types';
import { device } from 'utils/device';

/**
 * DOM side of the custom scrollbar: the thumb/track element ref
 * (.scrollbar__bar), the sizing math (bar height / max bar scroll / hide
 * threshold), the bar resize / sync tweens and the bar-drag position math.
 * Event wiring (mousedown / mousemove / mouseup registration) stays in
 * ScrollbarController (Events.on registrations live there; handlers delegate
 * here). Engine interop is resolved in the controller via the injected deps.
 */
export class ScrollbarView {
	private _dom: DOMHelper;
	private _deps: ScrollbarViewDeps;

	private _$bar: HTMLElement;

	public innerHeight: number;
	public hideScroller: boolean = true;

	private _barHeight: number;
	private _maxBarScroll: number;
	public barY: number = 0;
	private _minBarScroll: number = 0;

	//Scrolling with bar
	private _dragStartClientY: number = 0;
	private _dragStartBarY: number = 0;

	constructor(dom: DOMHelper, deps: ScrollbarViewDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	/** Query the bar element ref (delegated from ScrollbarController.DOMReady) */
	public queryRefs(container: Element): void {
		this._$bar = container.querySelector('.scrollbar__bar');
	}

	/** Set sizing after resize of retarget */
	public setSizing(): void {
		this.innerHeight = device.innerHeight;
		this.hideScroller = this._deps.getHeightTarget().clientHeight < this.innerHeight;

		if (this.hideScroller) {
			this._dom.removeClass(this._deps.getContainer(), '-active');
			return;
		}

		this.innerHeight = device.innerHeight;

		let ratio: number = this.innerHeight / this._deps.getHeightTarget().clientHeight;
		let extraScrollArea =
			this._deps.getExtraHeightTarget() && this._deps.getScrollerType() != ScrollerType.NATIVE
				? this._deps.getExtraHeightTarget().clientHeight
				: 0;
		extraScrollArea = 0;

		this._deps.setMaxTargetScroll(-1 * (this._deps.getHeightTarget().clientHeight - this.innerHeight + extraScrollArea));
		this._barHeight = this.innerHeight * ratio;
		this._maxBarScroll = this.innerHeight - this._barHeight - 20;

		this._dom.addClass(this._deps.getContainer(), '-active');
	}

	/** Resize the bar */
	public resizeBar(animateTime: number = 0, reactivateAfterResize: boolean = false): void {
		let completeCallback: any = reactivateAfterResize
			? () => {
					this._deps.onReactivate();
				}
			: null;
		gsap.TweenMax.to(this._$bar, animateTime, { y: this.barY, height: this._barHeight, onComplete: completeCallback });
	}

	/** Record the drag start positions (delegated from ScrollbarController._onBarClick) */
	public startBarDrag(clientY: number): void {
		this._dragStartBarY = this.barY;
		this._dragStartClientY = clientY;
	}

	/** On drag of bar update the scroll position (delegated from ScrollbarController._onBarDrag) */
	public barDrag(clientY: number): void {
		this.barY = constrain(this._dragStartBarY + (clientY - this._dragStartClientY), this._minBarScroll, this._maxBarScroll);
		gsap.TweenMax.set(this._$bar, { y: this.barY });

		//Set the target Y position of the scroller to move the plane:
		this._deps.setPlaneTargetY(this._deps.getMaxTargetScroll() * (this.barY / this._maxBarScroll));
	}

	/** Sync the bar to TargetY position -  when scrollweheel or arrrows are used  */
	public syncBarToTargetY(time: number = 0.3, targetY: number = null, ease: any = null): void {
		var ratio: number;
		if (targetY !== null) {
			ratio = targetY / this._deps.getMaxTargetScroll();
		} else {
			ratio = this._deps.getPlaneTargetY() / this._deps.getMaxTargetScroll();
		}

		if (!ease) {
			ease = gsap.Power3.easeOut;
		}

		this.barY = ratio * this._maxBarScroll;
		gsap.TweenMax.to(this._$bar, time, { y: this.barY, ease: ease });
	}
}
